import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Edit, Eye, Trash2, Plus, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const RESEEDABLE_SLUGS = new Set(['insurance', 'home', 'pricing']);

/** Single page card on the CMS "Pages" tab.
 *  • Shows page status (Published / Draft) + block count when the page exists.
 *  • Renders a tinted "Create" CTA when the slug is a default-template entry
 *    that hasn't been instantiated yet (page === null/undefined).
 *  • For seed-backed pages (insurance/home/pricing) — exposes a "Re-seed"
 *    action that calls /api/admin/cms/reseed-page/{slug} to apply the
 *    latest brochure/content from the seed script source-of-truth without
 *    waiting for a backend redeploy.
 */
export function PageCard({ page, title, description, slug, Icon, onEdit, onDelete, color }) {
  const exists = !!page?.id;
  const canReseed = exists && RESEEDABLE_SLUGS.has(slug);
  const { api } = useAuth();
  const [reseeding, setReseeding] = useState(false);

  const handleReseed = async () => {
    if (!window.confirm(
      `Re-seed /${slug} from the latest seed script?\n\nThis OVERWRITES the page's blocks with the version checked into code. Any edits made via the CMS editor will be lost.`,
    )) return;
    setReseeding(true);
    try {
      const { data } = await api.post(`/api/admin/cms/reseed-page/${slug}`);
      toast.success(data?.applied ? `/${slug} re-seeded with the latest content.` : `/${slug} already at the latest version.`);
      // Force a hard refresh so the parent table re-fetches updated block count.
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-seed failed');
    } finally {
      setReseeding(false);
    }
  };

  return (
    <Card className={`bg-[#041A3B] border-white/10 ${exists ? '' : 'opacity-70'}`} data-testid={`page-card-${slug}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}1A` }}>
            <Icon className="w-6 h-6" style={{ color }} />
          </div>
          {exists && (
            <Badge className={page.is_published ? 'bg-[#00E676]/20 text-[#00E676]' : 'bg-[#5C7599]/20 text-[#5C7599]'}>
              {page.is_published ? 'Published' : 'Draft'}
            </Badge>
          )}
        </div>
        <h3 className="font-outfit text-lg font-semibold text-white mb-1">{title}</h3>
        <p className="text-[#5C7599] text-sm mb-2 line-clamp-2">{description}</p>
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-mono" style={{ color }}>/{slug}</p>
          {exists && (
            <p className="text-[10px] text-[#5C7599]">
              {(page.sections || []).length} block{(page.sections || []).length === 1 ? '' : 's'}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {exists ? (
            <>
              <Button size="sm" onClick={onEdit} className="flex-1 btn-secondary" data-testid={`edit-page-${slug}`}>
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </Button>
              <Button size="sm" variant="ghost" asChild className="text-[#A0B4CC]">
                <a href={`/page/${slug}`} target="_blank" rel="noopener noreferrer" data-testid={`view-page-${slug}`}>
                  <Eye className="w-4 h-4" />
                </a>
              </Button>
              {canReseed && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleReseed}
                  disabled={reseeding}
                  className="text-[#FFB300]"
                  title="Re-seed this page from the code seed script"
                  data-testid={`reseed-page-${slug}`}
                >
                  <RefreshCw className={`w-4 h-4 ${reseeding ? 'animate-spin' : ''}`} />
                </Button>
              )}
              {onDelete && (
                <Button size="sm" variant="ghost" onClick={onDelete} className="text-[#FF1744]">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </>
          ) : (
            <Button size="sm" onClick={onEdit} className="w-full btn-primary" data-testid={`create-page-${slug}`}>
              <Plus className="w-4 h-4 mr-1" />
              Create
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default PageCard;
